/*
 * Where every page sits in the story.
 *
 * The nav grew to 50 links inside a shape built for far fewer: five top-level
 * menus, eleven nested submenus, three levels deep on hover, and 37 of the 50
 * links buried in the two menus that had swollen to ~19 items each while
 * another held two. It grouped by book order -- Torah, Histories, Wisdoms,
 * Major Prophets -- which is how a Bible is printed, not how anyone reads it.
 *
 * These groups follow the narrative instead, so a reader can find a page by
 * remembering roughly when it happens. Explorers, lineages and timelines for
 * one era sit together rather than being split across "Old Testament" and
 * "People of the Bible".
 *
 * One structure, three consumers: the header nav, the /explorers index, and a
 * mocha guard that every route App.js serves appears here exactly once. That
 * guard is why this file is data rather than JSX -- the old nav was hand-built
 * markup, and nothing could tell you it had listed /events twice.
 *
 * `accent` is only used by the /explorers index, to colour-code the story.
 * Every value is taken from the palette already in App.css -- primary,
 * warning, success, purple, orange, danger -- with cyan and teal added to
 * fill the set. Nothing new was invented for the brand.
 *
 * `label` is only for when the nav should read shorter than the page's own
 * title ("Genesis" against "Genesis Explorer"). Left off, the title from
 * siteSearchIndex is used, so titles are not maintained in two places.
 */

export const NAV_GROUPS = [
  {
    id: "beginnings",
    accent: "#2563eb",
    label: "Beginnings",
    blurb: "Creation, the flood, and the families the covenant runs through.",
    items: [
      { route: "/genesis", label: "Genesis" },
      { route: "/adam-noah", label: "Adam to Noah" },
      { route: "/japheth-lineage", label: "Japheth's Line" },
      { route: "/ham-lineage", label: "Ham's Line" },
      { route: "/shem-lineage", label: "Shem's Line" },
      { route: "/abraham-lineage", label: "Abraham's Line" },
      { route: "/nahor-lineage", label: "Nahor's Line" },
      { route: "/haran-lineage", label: "Haran's Line" },
      { route: "/jacob-lineage", label: "Jacob's Line" },
      { route: "/esau-lineage", label: "Esau's Line" },
    ],
  },
  {
    id: "exodus-and-law",
    accent: "#f59e0b",
    label: "Exodus & the Law",
    blurb: "Leaving Egypt, the wilderness years, and the priesthood.",
    items: [
      { route: "/exodus", label: "Exodus" },
      { route: "/leviticus", label: "Leviticus" },
      { route: "/numbers", label: "Numbers" },
      { route: "/exodus-deuteronomy", label: "Wilderness Journey" },
      { route: "/moses-aaron", label: "Moses & Aaron" },
      { route: "/levi-line", label: "Levitical Priesthood" },
    ],
  },
  {
    id: "conquest-and-kingdom",
    accent: "#16a34a",
    label: "Conquest & Kingdom",
    blurb: "Taking the land, the judges, and the kings who followed.",
    items: [
      { route: "/joshua", label: "Joshua" },
      { route: "/judges", label: "Judges" },
      { route: "/1samuel-2chron", label: "United to Divided Kingdoms" },
      { route: "/saul-lineage", label: "Saul's Line" },
      { route: "/david-lineage", label: "David's Line" },
      { route: "/kings-of-judah", label: "Kings of Judah" },
    ],
  },
  {
    id: "exile-and-return",
    accent: "#9d0191",
    label: "Exile & Return",
    blurb: "The prophets who warned, and the people who came home.",
    items: [
      { route: "/isaiah", label: "Isaiah" },
      { route: "/jeremiah", label: "Jeremiah" },
      { route: "/ezekiel", label: "Ezekiel" },
      { route: "/daniel", label: "Daniel" },
      { route: "/minor-prophets", label: "Minor Prophets" },
      { route: "/ezra-nehemiah-esther", label: "Post-Exile History" },
    ],
  },
  {
    /*
     * The honest exception to story order. These books sit alongside the
     * narrative rather than at a point in it -- Psalms spans the whole
     * monarchy, Job is placed nowhere at all -- so filing them under an era
     * would be a guess the reader would have to reverse-engineer.
     */
    id: "wisdom-and-poetry",
    accent: "#0891b2",
    label: "Wisdom & Poetry",
    blurb: "The books that run alongside the story rather than through it.",
    items: [
      { route: "/job", label: "Job" },
      { route: "/psalms", label: "Psalms" },
      { route: "/proverbs", label: "Proverbs" },
      { route: "/ecclesiastes", label: "Ecclesiastes" },
      { route: "/song-of-songs", label: "Song of Songs" },
    ],
  },
  {
    id: "between-the-testaments",
    accent: "#ff7722",
    label: "Between the Testaments",
    blurb: "The four centuries the Bible does not narrate.",
    items: [
      { route: "/between-the-testaments", label: "Between the Testaments" },
      { route: "/david-alexander", label: "David → Alexander" },
      { route: "/alexander-earlychurch", label: "Alexander → Early Church" },
      { route: "/second-temple-leaders", label: "Second Temple Leaders" },
      { route: "/who-ruled-the-biblical-land", label: "Who Ruled the Land" },
    ],
  },
  {
    id: "jesus-and-the-church",
    accent: "#1d4ed8",
    label: "Jesus & the Church",
    blurb: "The gospels, the first churches, and the letters they were sent.",
    items: [
      { route: "/walk-through-the-gospels", label: "The Gospels" },
      { route: "/church-history", label: "Church History through Acts" },
      { route: "/early-church-history", label: "Early Church History" },
      { route: "/early-church-fathers", label: "Early Church Fathers" },
      { route: "/church-letters", label: "Church Letters" },
      { route: "/revelation", label: "Revelation" },
    ],
  },
  {
    id: "people-and-nations",
    accent: "#e36436",
    label: "People & Nations",
    blurb: "Groups whose story crosses every era.",
    items: [
      { route: "/women-of-the-bible", label: "Women of the Bible" },
      { route: "/nations-of-the-bible", label: "Nations of the Bible" },
      { route: "/enemies-of-the-bible", label: "Enemies of the Bible" },
    ],
  },
  {
    id: "events",
    accent: "#475569",
    label: "Events",
    blurb: "Every recorded event, searchable and filterable.",
    items: [{ route: "/events", label: "Biblical Events Explorer" }],
  },
  {
    id: "scripture-itself",
    accent: "#0f766e",
    label: "Scripture Itself",
    blurb: "How the text was preserved, and how it came to be recognised.",
    items: [
      { route: "/scripture-preservation", label: "Preservation of Scripture" },
      {
        route: "/scripture-formation-recognition",
        label: "Formation & Recognition",
      },
    ],
  },
];

/*
 * The header can hold a handful of entries, not ten. Each section opens one
 * panel with its groups as columns -- so the groups are headings a reader
 * scans, not submenus they have to hover into.
 *
 * That is the real fix for the old nav: depth drops from three levels to two,
 * and the diagonal mouse path between a parent menu and its submenu is gone.
 */
export const NAV_SECTIONS = [
  {
    id: "old-testament",
    label: "Old Testament",
    groups: [
      "beginnings",
      "exodus-and-law",
      "conquest-and-kingdom",
      "exile-and-return",
      "wisdom-and-poetry",
    ],
  },
  {
    id: "new-testament",
    label: "New Testament",
    /* The intertestamental period sits here because it is what the gospels
       open into, not because it is New Testament writing. */
    groups: ["between-the-testaments", "jesus-and-the-church"],
  },
  {
    id: "across-scripture",
    label: "Across Scripture",
    groups: ["people-and-nations", "events", "scripture-itself"],
  },
];

/* The groups of one section, in the order the section lists them. */
export function groupsForSection(sectionId) {
  const section = NAV_SECTIONS.find((entry) => entry.id === sectionId);
  if (!section) return [];

  return section.groups
    .map((groupId) => NAV_GROUPS.find((group) => group.id === groupId))
    .filter(Boolean);
}

/* Every route the navigation places, in story order. */
export const NAV_ROUTES = NAV_GROUPS.flatMap((group) =>
  group.items.map((item) => item.route)
);

const ROUTE_LABELS = new Map(
  NAV_GROUPS.flatMap((group) =>
    group.items.map((item) => [item.route, item.label])
  )
);

/*
 * The short label the nav gives a route, or null when the nav does not place
 * it. Null rather than the route itself so a caller can fall back to the page
 * title, which reads better than a slug.
 */
export function labelForRoute(route) {
  return ROUTE_LABELS.get(route) || null;
}

export default NAV_GROUPS;
